/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import sizes from 'constants/sizes';
import employeesSource from 'constants/employeesSource';

import { useTranslate } from 'hooks/useTranslate';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { getTotalEmployeesLinkedToBeneficiary } from 'utils/employees';

import transformData from 'modules/_shared/components/Tables/helpers';
import AddEmployeesToPlan from 'modules/_shared/components/Modals/AddEmployeesToPlan';
import SyncEmployeesModal from 'modules/beneficiaries/modals/SyncEmployees';
import {
  getEmployeesInfo,
  getSocietyEmployees,
  saveEmployees,
} from 'modules/beneficiaries/redux/employeeActions';
import { setModal } from 'modules/_shared/redux/modalActions';
import { getStockPlans } from 'modules/beneficiaries/redux/planActions';
import { parseEmployeesData } from 'modules/beneficiaries/utils/parseEmployeesData';
import { getEmployeesPageDescription } from 'modules/beneficiaries/utils/getEmployeesPageDescription';
import { useGetPlanSectionPermissions } from 'modules/beneficiaries/hooks/useGetPlanSectionPermissions';

import menuTypes from 'constants/menuTypes';
import tableColumns from '../utils/tableColumns';

const isEnabledAndValidated = (integration) =>
  integration.enabled && integration.validated;

export const useEmployeeList = () => {
  const { t, i18n } = useTranslate();
  const dispatch = useDispatch();
  const { societyId } = useParams();

  const { isAdmin, isReadOnly } = useGetPlanSectionPermissions().data;

  const employees = useSelector((state) => state.employees);
  const actualSociety = useSelector((state) => state.society?.actualSociety);
  const plans = useSelector((state) => state.plans);

  const [isLoading, setIsLoading] = useState(true);
  const isFetchingRef = useRef(false);
  const [tableData, setTableData] = useState([]);
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [kpiFilter, setKpiFilter] = useState('active');

  useGetMenu(menuTypes.EXTENDED);
  useGetActualSociety(societyId);

  const beneficiariesExternalIdList = useMemo(() => {
    const ids = actualSociety?.beneficiaries
      ?.map((b) => b?.employeeExternalId ?? null)
      ?.filter(Boolean) ?? [];
    return [...new Set(ids)];
  }, [actualSociety]);

  useEffect(() => {
    if (!employees?.length) {
      return;
    }

    const newData = parseEmployeesData({
      employees,
      beneficiariesExternalId: beneficiariesExternalIdList,
      society: actualSociety,
      t,
    });

    if (newData?.length > 0) {
      setTableData(
        transformData({
          data: newData,
          columns: tableColumns(i18n),
        })
      );
    }

    setActiveEmployees(
      employees?.filter((employee) => !employee.terminationDate) || []
    );
  }, [employees, beneficiariesExternalIdList, actualSociety, i18n.language]);

  const fetchEmployees = useCallback(async () => {
    if (!societyId) {
      setIsLoading(false);
      return;
    }
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      await dispatch(getSocietyEmployees(societyId));
    } catch (error) {
      console.error('fetchEmployees', error); // eslint-disable-line no-console
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [societyId, dispatch]);

  useEffect(() => {
    dispatch(getStockPlans(societyId));
    fetchEmployees();
  }, [societyId, fetchEmployees]);

  const currentTableColumns = useMemo(
    () => tableColumns(i18n),
    [i18n.language]
  );

  const pageDescription = useMemo(
    () =>
      getEmployeesPageDescription({
        society: actualSociety,
        employees,
        t,
      }),
    [actualSociety, employees, i18n.language]
  );

  const inactiveEmployees = useMemo(
    () => employees?.filter((e) => e.terminationDate) ?? [],
    [employees]
  );

  const activeEmployeesWithIncentive = useMemo(
    () =>
      getTotalEmployeesLinkedToBeneficiary(
        activeEmployees,
        beneficiariesExternalIdList
      ),
    [activeEmployees, beneficiariesExternalIdList]
  );

  const inactiveEmployeesWithIncentive = useMemo(
    () =>
      getTotalEmployeesLinkedToBeneficiary(
        inactiveEmployees,
        beneficiariesExternalIdList
      ),
    [inactiveEmployees, beneficiariesExternalIdList]
  );

  const filteredTableData = useMemo(() => {
    if (kpiFilter === 'active') {
      return tableData.filter((row) => row.isActive === true);
    }
    if (kpiFilter === 'activeWithIncentives') {
      return tableData.filter(
        (row) => row.isActive === true && row.hasIncentivePlan === true
      );
    }
    if (kpiFilter === 'inactiveWithIncentives') {
      return tableData.filter(
        (row) => row.isActive === false && row.hasIncentivePlan === true
      );
    }
    return tableData.filter((row) => row.isActive === true);
  }, [tableData, kpiFilter]);

  const handleKpiFilterClick = useCallback((filter) => {
    setKpiFilter((prev) => (prev === filter ? null : filter));
  }, []);

  const handleSyncEmployees = useCallback(() => {
    dispatch(setModal(<SyncEmployeesModal size={sizes.XL} />));
  }, [dispatch]);

  const handleFilterEmployees = useCallback(() => {
    if (isReadOnly) return;
    if (plans?.length) {
      dispatch(setModal(<AddEmployeesToPlan size={sizes.LG} />));
    } else {
      return Swal.fire({
        icon: 'warning',
        title: `<h4 class="nk-modal-title">${t('HasNoPlans')}</h4>`,
        html: `<p class="text-soft px-4">${t('HasNoPlansDescription')}</p>
        <p class="text-primary" style="margin-top:24px;margin-bottom:30px">
          ${t('PlansListPage')}
        </p>`,
        confirmButtonText: t('OK'),
        allowOutsideClick: false,
        showCancelButton: false,
        confirmButtonColor: '#6576FF',
      });
    }
  }, [dispatch, isReadOnly, plans?.length, t]);

  const handleLoadEmployees = useCallback(async () => {
    try {
      if (isReadOnly) return;

      setIsLoading(true);

      const integrations = {};
      Object.entries(actualSociety?.integrations)?.forEach(([key, value]) => {
        if (isEnabledAndValidated(value) && value.token) {
          integrations[key] = value.token;
        }
      });

      const hasValidKeys = Object.keys(integrations).length > 0;

      if (hasValidKeys) {
        let employeesList = [];

        if (integrations.factorial) {
          employeesList = await getEmployeesInfo(
            employeesSource.FACTORIAL,
            employees,
            actualSociety
          );
        }

        setIsLoading(false);

        if (employeesList.newEmployees || employeesList.updatedEmployees) {
          return Swal.fire({
            icon: 'info',
            title: `<h4 class="nk-modal-title">${t(
              'EmployeesImportModalTitle'
            )}</h4>`,
            html: `<p class="text-soft px-4" style="margin-top:24px;margin-bottom:30px">
            ${t('EmployeesImportModalDescription', {
              newEmployees: employeesList.newEmployees?.length,
              updatedEmployees: employeesList.updatedEmployees?.length,
            })}
          </p>`,
            confirmButtonText: t('EmployeesImportModalConfirmButtonText'),
            confirmButtonColor: '#6576ff',
            allowOutsideClick: false,
            showCancelButton: true,
          }).then((result) => {
            if (result.isConfirmed) {
              dispatch(
                saveEmployees(
                  employeesList,
                  employeesSource.FACTORIAL,
                  actualSociety['_id']
                )
              );
            }
          });
        }
      } else {
        return Swal.fire({
          icon: 'warning',
          title: `<h4 class="nk-modal-title">${t('HasNotValidKeys')}<br></h4>`,
          html: `<p class="text-soft px-4">${t(
            'HasNotValidKeysDescription'
          )}</p>
        <p class="text-primary" style="margin-top:24px;margin-bottom:30px">
          ${t('IntegrationsTabAndPage')}
        </p>`,
          confirmButtonText: t('OK'),
          allowOutsideClick: false,
          showCancelButton: false,
          confirmButtonColor: '#6576FF',
        });
      }
    } catch (error) {
      console.error('handleLoadEmployees', error); // eslint-disable-line no-console
    } finally {
      setIsLoading(false);
    }
  }, [
    dispatch,
    isReadOnly,
    actualSociety,
    employees,
    t,
  ]);

  return {
    data: {
      actualSociety,
      tableData: filteredTableData,
      currentTableColumns,
      kpiFilter,
      activeEmployees,
      employees,
      isAdmin,
      isReadOnly,
      pageDescription,
      activeEmployeesWithIncentive,
      inactiveEmployeesWithIncentive,
    },
    actions: {
      handleLoadEmployees,
      handleFilterEmployees,
      handleSyncEmployees,
      handleKpiFilterClick,
    },
    isLoading,
    isError: false,
  };
};
